/**
 * 🔒 2026-09-21 (Manak) — Stock Overview round, spec R7.
 * One filter bar for both Stock Overview tables: Category → Sub-category, and Product type.
 * Sub-category choices follow the chosen category; changing the category clears it.
 */
import { useEffect, useState } from 'react';
import { api } from '@/utils/api';

export interface StockFilterValue { category: string; subCategory: string; productType: '' | 'Traded' | 'Manufactured' }
export const EMPTY_STOCK_FILTER: StockFilterValue = { category: '', subCategory: '', productType: '' };

type Options = { categories: Array<{ name: string; subCategories: string[] }>; productTypes: string[] };

/** Query params for GET /atp and GET /products — only the filters that are set. */
export function stockFilterParams(f: StockFilterValue): Record<string, string> {
  const out: Record<string, string> = {};
  if (f.category) out.category = f.category;
  if (f.subCategory) out.subCategory = f.subCategory;
  if (f.productType) out.productType = f.productType;
  return out;
}

let cached: Promise<Options> | null = null;
function loadOptions(): Promise<Options> {
  cached ??= api.get('/products/filter-options').then((r) => {
    // A wrong-shaped reply must never crash the page this bar sits on — treat it as "no choices".
    const d = r?.data?.data;
    const cats = Array.isArray(d?.categories) ? d.categories : [];
    return {
      categories: cats
        .filter((c: any) => c && typeof c.name === 'string')
        .map((c: any) => ({ name: c.name, subCategories: Array.isArray(c.subCategories) ? c.subCategories.filter((x: any) => typeof x === 'string') : [] })),
      productTypes: ['Traded', 'Manufactured'],
    } as Options;
  }).catch((e) => { cached = null; throw e; });
  return cached;
}
/** Test hook: forget the cached options. */
export function resetStockFilterCache() { cached = null; }

export function StockFilters({ value, onChange, label }: { value: StockFilterValue; onChange: (v: StockFilterValue) => void; label: string }) {
  const [opts, setOpts] = useState<Options>({ categories: [], productTypes: ['Traded', 'Manufactured'] });
  useEffect(() => {
    let alive = true;
    loadOptions().then((o) => { if (alive) setOpts(o); }).catch(() => { /* filters stay usable with no choices */ });
    return () => { alive = false; };
  }, []);
  const subs = opts.categories.find((c) => c.name === value.category)?.subCategories ?? [];
  const cls = 'rounded-lg border border-gray-200 bg-white py-1.5 px-2 text-sm text-gray-700';
  return (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label={`${label} filters`}>
      <select aria-label={`${label} category`} className={cls} value={value.category}
        onChange={(e) => onChange({ ...value, category: e.target.value, subCategory: '' })}>
        <option value="">All categories</option>
        {opts.categories.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
      </select>
      <select aria-label={`${label} sub-category`} className={cls} value={value.subCategory} disabled={!value.category || subs.length === 0}
        onChange={(e) => onChange({ ...value, subCategory: e.target.value })}>
        <option value="">All sub-categories</option>
        {subs.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <select aria-label={`${label} type`} className={cls} value={value.productType}
        onChange={(e) => onChange({ ...value, productType: e.target.value as StockFilterValue['productType'] })}>
        <option value="">All types</option>
        <option value="Traded">Traded</option>
        <option value="Manufactured">Manufactured</option>
      </select>
    </div>
  );
}
